/**
 * Motion tokens (§10 of the design spec). Import these instead of hard-coding durations/easings.
 *   duration.* are seconds for framer-motion; use `${duration.fast * 1000}ms` for CSS.
 *   Every framer animation must be gated by `useReducedMotion()` (below) — pass `reducedTransition` when it is on.
 */
import { usePrefersReducedMotion } from "@chakra-ui/react";

/**
 * Client-only reduced-motion hook. Chakra's `usePrefersReducedMotion()` defaults to `ssr: true`, which returns
 * `false` on the first render and only corrects itself in an effect — too late for framer's mount-only `initial`.
 * CRA renders on the client only, so reading the media query synchronously is safe (no hydration mismatch).
 */
export const useReducedMotion = (): boolean => usePrefersReducedMotion({ ssr: false });

export type CubicBezier = [number, number, number, number];

export const duration = {
  instant: 0.08,
  fast: 0.16,
  normal: 0.24,
  slow: 0.36,
  story: 0.5,
  storyLong: 0.9,
} as const;

export const ease: Record<"standard" | "emphasized" | "exit", CubicBezier> = {
  standard: [0.2, 0.8, 0.2, 1],
  emphasized: [0.22, 1, 0.36, 1],
  exit: [0.4, 0, 1, 1],
};

export const spring = {
  /** nav pill, spend segment */
  snappy: { type: "spring", stiffness: 420, damping: 34 } as const,
  /** wizard rail */
  soft: { type: "spring", stiffness: 120, damping: 20 } as const,
};

/** Use as the framer `transition` when `useReducedMotion()` is true. */
export const reducedTransition = { duration: 0 } as const;

/** CSS shorthand for hover lifts on plain elements. */
export const cssTransition = {
  transform: `transform ${duration.fast * 1000}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
  colors: `background-color ${duration.instant * 1000}ms, color ${duration.instant * 1000}ms, border-color ${duration.instant * 1000}ms, box-shadow ${duration.instant * 1000}ms`,
};
